/**
 * Her file, and what fills it in.
 *
 * The dossier in Settings has been four fixed lines since she shipped: a name,
 * a job title, a status, three informants. It reads well once. The problem
 * with a personnel file that never gains a page is that it is scenery — there
 * is no reason to open it a second time, so nobody does, so the one place in
 * the app that is purely about *her* is also the one nobody visits.
 *
 * So it accumulates. Four sections, three of which are derived from things the
 * app already knows and one of which is recorded as it happens:
 *
 * - **Tarnybos laikas** — how long this account has known her. Redacted fields
 *   that fill in at thirty days, a hundred, and a year.
 * - **Incidentai** — one line per trophy earned, with the date.
 * - **Bylos** — her running bits, closed as each saga is followed to its end.
 * - **Radiniai** — the easter eggs, listed as redacted rows whether or not
 *   they have been found, so the shape of what is missing is visible without
 *   anything being given away.
 *
 * Only the finds are stored (see `moments.ts`); everything else is computed
 * from the diary, the awards table and the date she was first spoken to.
 */

import { SAGAS } from '@gramas/shared'

/** Blanked-out text for a field that has not been earned yet. */
export const REDACTED = '███████'

export interface ServiceEntry {
  /** Days of acquaintance this unlocks at. */
  after: number
  label: string
  /** Shown once `after` days have passed; before that the value is redacted. */
  value: string
}

/**
 * The file filling itself in as the acquaintance lengthens.
 *
 * The thresholds are the ones the app already treats as meaningful elsewhere —
 * a month, a hundred days, a year — so the dossier and the trophy cabinet are
 * telling the same story at the same pace rather than two overlapping ones.
 */
export const SERVICE: readonly ServiceEntry[] = [
  { after: 0, label: 'Pareigos', value: 'pomirtinio pasaulio dietologė' },
  { after: 0, label: 'Statusas', value: 'neaiškus nuo 1863 m.' },
  {
    after: 0,
    label: 'Informatoriai',
    value: 'šaldytuvas, svarstyklės, orkaitė; vakarais — virdulys',
  },
  {
    after: 30,
    label: 'Darbo vieta',
    value: 'virtuvė; naktimis — visas butas',
  },
  {
    after: 30,
    label: 'Ankstesnė byla',
    value: 'aštuoneri metai be glitimo, be įrodymų',
  },
  {
    after: 100,
    label: 'Kilmė',
    value: 'susidarė iš įsitikinimo, kurio niekas netikrino',
  },
  {
    after: 100,
    label: 'Priešiškumas',
    value: 'indaplovė — nėra ir nebus',
  },
  {
    after: 365,
    label: 'Mėgstamiausia data',
    value: 'lapkričio antroji',
  },
  {
    after: 365,
    label: 'Pastaba byloje',
    value: 'prisirišo; oficialiai to neigia',
  },
]

export interface FindDef {
  id: string
  /** What it was, revealed only once found. */
  label: string
  /** A hint at when, vague on purpose. */
  when: string
}

/**
 * Every findable moment, listed whether or not it has been found.
 *
 * The unfound ones are the point: five redacted rows say there is something to
 * look for, which is a different feeling from an empty list. The `when` hint
 * is deliberately imprecise — "a particular minute" rather than 03:33 — so the
 * row narrows the search without ending it.
 */
export const FINDS: readonly FindDef[] = [
  { id: 'velines', label: 'Vėlinės', when: 'jos diena' },
  { id: 'kates-gimtadienis', label: 'Katės gimtadienis', when: 'kartą per metus' },
  { id: 'trylika', label: 'Penktadienis, tryliktoji', when: 'kelis kartus per metus' },
  { id: 'trys-trisdesimt-trys', label: 'Trys trisdešimt trys', when: 'viena minutė per parą' },
  { id: 'vidurnaktis', label: 'Lygiai vidurnaktis', when: 'viena minutė per parą' },
  { id: 'nauja-byla', label: 'Mėnesio pirmoji', when: 'kartą per mėnesį, ryte' },
  { id: 'simetrija', label: 'Dvidešimt dvi dvidešimt dvi', when: 'viena minutė per parą' },
  { id: 'leet', label: 'Trylika trisdešimt septynios', when: 'viena minutė per parą' },
]

/** A saga's title in the file, for the ones that have been seen to the end. */
export const SAGA_TITLE: Readonly<Record<string, string>> = {
  'saga.kempine': 'Kempinės byla',
  'saga.siurblys': 'Siurblio byla',
  'saga.skaicius': 'Skaičiavimų byla',
  'saga.gaudesys': 'Gaudesio byla',
  'saga.saukstelis': 'Šaukštelio byla',
  'saga.kvapas': 'Kvapo byla',
  'saga.dulkes': 'Dulkių byla',
}

/** Every saga id, so the file can show the closed ones against the total. */
export const SAGA_IDS: readonly string[] = SAGAS.map((saga) => saga.id)

/** Whole days between two ISO timestamps; 0 when the first is missing. */
export function daysKnown(metAt: string | null, now: Date = new Date()): number {
  if (metAt == null) return 0
  const met = new Date(metAt)
  if (Number.isNaN(met.getTime())) return 0
  return Math.max(0, Math.floor((now.getTime() - met.getTime()) / 86_400_000))
}
